import React, { useEffect, useRef } from 'react';
import { FaHeart, FaMapMarkerAlt, FaStar, FaChevronLeft, FaChevronRight, FaEye } from 'react-icons/fa';
import RestaurantCard from './Card';
import type { Restaurant } from '../../Manager/restaurant.types';

interface RestaurantSectionProps {
  title: string;
  restaurants: Restaurant[];
  loading?: boolean;
  onCardClick: (id: string, name: string) => void;
}

const RestaurantSection: React.FC<RestaurantSectionProps> = ({
  title,
  restaurants = [],
  loading = false,
  onCardClick,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollSection = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = direction === 'left' ? -300 : 300;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <section className="mb-2 md:mb-6 relative">
      <div className="flex justify-between items-center pb-2">
        {/* Title skeleton when loading */}
        {loading ? (
          <div className="h-6 w-48 bg-gray-200 rounded-md ml-2 animate-pulse"></div>
        ) : (
          <h2 className="text-lg ml-2 font-bold poppins-semibold text-[#bf491e]">
            {title}
          </h2>
        )}
        
        <div className="flex gap-2">
          <button
            onClick={() => scrollSection('left')}
            className="p-2 rounded-full bg-white border border-[#FFDDA6] hover:bg-[#FFF8E7] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading}
          >
            {loading ? (
              <div className="w-4 h-4 bg-gray-200 rounded-full animate-pulse"></div>
            ) : (
              <FaChevronLeft className="text-[#5E503F]" />
            )}
          </button>
          <button
            onClick={() => scrollSection('right')}
            className="p-2 rounded-full bg-white border border-[#FFDDA6] hover:bg-[#FFF8E7] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading}
          >
            {loading ? (
              <div className="w-4 h-4 bg-gray-200 rounded-full animate-pulse"></div>
            ) : (
              <FaChevronRight className="text-[#5E503F]" />
            )}
          </button>
        </div>
      </div>
      
      <div 
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto scrollbar-hide pb-4"
        style={{
          msOverflowStyle: 'none',
          scrollbarWidth: 'none'
        }}
      >
        {loading ? (
          // Enhanced skeleton loaders that match RestaurantCard structure
          Array.from({ length: 4 }).map((_, i) => (
            <div 
              key={i} 
              className="flex-shrink-0 w-64 bg-white rounded-3xl shadow-md overflow-hidden cursor-pointer animate-pulse"
            >
              {/* Image skeleton */}
              <div className="h-48 bg-gray-200 relative">
                {/* Image overlay skeleton */}
                <div className="absolute top-3 right-3 w-8 h-8 bg-gray-300 rounded-full"></div>
              </div>
              
              {/* Content skeleton */}
              <div className="p-4">
                {/* Title skeleton */}
                <div className="h-5 bg-gray-200 rounded w-3/4 mb-3"></div>
                
                {/* Rating skeleton */}
                <div className="flex items-center mb-2">
                  <div className="flex mr-2">
                    {[...Array(5)].map((_, starIdx) => (
                      <div 
                        key={starIdx} 
                        className="w-4 h-4 bg-gray-200 rounded-full mr-1"
                      ></div>
                    ))}
                  </div>
                  <div className="h-4 bg-gray-200 rounded w-10"></div>
                </div>
                
                {/* Location skeleton */}
                <div className="flex items-center mb-3">
                  <div className="w-4 h-4 bg-gray-200 rounded mr-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-32"></div>
                </div>
                
                {/* Footer info skeleton */}
                <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                  <div className="flex items-center">
                    <div className="w-5 h-5 bg-gray-200 rounded mr-1"></div>
                    <div className="h-4 bg-gray-200 rounded w-16"></div>
                  </div>
                  <div className="flex items-center">
                    <div className="w-5 h-5 bg-gray-200 rounded mr-1"></div>
                    <div className="h-4 bg-gray-200 rounded w-12"></div>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          restaurants.map(restaurant => (
            <RestaurantCard 
              key={restaurant._id} 
              restaurant={restaurant}
              onCardClick={onCardClick}
            />
          ))
        )}
      </div>
      
      {/* Safety: Show empty state when no restaurants */}
      {!loading && restaurants && restaurants.length === 0 && (
        <div className="text-center py-8">
          <div className="text-gray-400 text-lg mb-2">No restaurants found</div>
          <div className="text-gray-500 text-sm">Try adjusting your search criteria</div>
        </div>
      )}
    </section>
  );
};

export default RestaurantSection;